'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './ChatWidget.module.css';

const WEBHOOK_URL =
  'https://heretic-perch.pikapod.net/webhook/1d16d3f8-32fa-4676-9d6d-dd63d5883b98/chat';
const WEBHOOK_ROUTE = 'general';
const WELCOME = 'مرحباً بك! 👋 معك أكسي، مساعدك الشخصي. كيف يمكنني مساعدتك اليوم؟';

type ChatMessage = {
  id: string;
  role: 'user' | 'bot';
  text: string;
  html?: string;
  error?: boolean;
};

function getChatId() {
  if (typeof window === 'undefined') return '';
  let chatId = sessionStorage.getItem('chatId');
  if (!chatId) {
    chatId = 'chat_' + Math.random().toString(36).slice(2, 11);
    sessionStorage.setItem('chatId', chatId);
  }
  return chatId;
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function renderMarkdown(raw: string) {
  const escaped = escapeHtml(raw);
  const withCode = escaped.replace(/`([^`]+)`/g, '<code>$1</code>');
  const withBold = withCode.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  const withBreaks = withBold.replace(/\n/g, '<br />');
  return withBreaks;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const [typing, setTyping] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { id: 'welcome', role: 'bot', text: WELCOME, html: WELCOME },
  ]);
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, typing, scrollToBottom]);

  const openWidget = () => {
    setOpen(true);
    setOpening(false);
    requestAnimationFrame(() => setOpening(true));
  };

  const closeWidget = () => {
    setOpen(false);
    setOpening(false);
  };

  const autoResize = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  };

  useEffect(() => {
    autoResize();
  }, [input, open]);

  const sendMessage = async () => {
    const message = input.trim();
    if (!message || sending) return;

    setPulsing(true);
    window.setTimeout(() => setPulsing(false), 250);
    setInput('');
    setSending(true);
    setTyping(true);
    setMessages((prev) => [
      ...prev,
      { id: `user-${Date.now()}`, role: 'user', text: message },
    ]);

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatId: getChatId(),
          message,
          route: WEBHOOK_ROUTE,
        }),
      });
      const data = await response.json();
      const rawOutput = data.output || 'عذراً، لم أفهم ذلك.';
      setMessages((prev) => [
        ...prev,
        {
          id: `bot-${Date.now()}`,
          role: 'bot',
          text: rawOutput,
          html: renderMarkdown(rawOutput),
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          role: 'bot',
          text: 'تعذّر الوصول إلى الخادم. حاول مرة أخرى.',
          error: true,
        },
      ]);
    } finally {
      setTyping(false);
      setSending(false);
    }
  };

  return (
    <div className={styles.root}>
      {!open && (
        <button
          type="button"
          className={styles.button}
          aria-label="فتح الدردشة"
          onClick={openWidget}
        >
          💬
          <span className={styles.statusDot} />
        </button>
      )}

      {open && (
        <div className={`${styles.container} ${opening ? styles.opening : ''}`}>
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <div className={styles.avatar}>
                <svg width="40" height="40" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="50" cy="50" r="46" fill="#0c0c0d" stroke="#ffb800" strokeWidth="8" />
                  <circle cx="50" cy="22" r="7" fill="#ffb800" />
                  <rect x="46.5" y="27" width="7" height="13" rx="3" fill="#ffb800" />
                  <rect x="22" y="48" width="12" height="28" rx="6" fill="#ffb800" />
                  <rect x="66" y="48" width="12" height="28" rx="6" fill="#ffb800" />
                  <rect x="27" y="38" width="46" height="50" rx="20" fill="#ffb800" />
                  <rect x="34" y="47" width="32" height="32" rx="14" fill="#0c0c0d" />
                  <path d="M41 62 Q46 56 51 62" stroke="#ffb800" strokeWidth="3.5" fill="none" strokeLinecap="round" />
                  <path d="M51 62 Q56 56 61 62" stroke="#ffb800" strokeWidth="3.5" fill="none" strokeLinecap="round" />
                </svg>
              </div>
              <div className={styles.titles}>
                <span lang="ar" dir="rtl">الدردشة مع أكسي</span>
                <span className={styles.subtitle} lang="ar" dir="rtl">
                  <i className={styles.dot} />
                  مساعدك الشخصي
                </span>
              </div>
            </div>
            <button type="button" className={styles.closeBtn} onClick={closeWidget} aria-label="إغلاق">
              ✖
            </button>
          </div>

          <div className={styles.waveform} aria-hidden="true">
            <span /><span /><span /><span /><span /><span /><span /><span /><span />
          </div>

          <div ref={bodyRef} className={styles.body}>
            {messages.map((msg) => {
              if (msg.role === 'user') {
                return (
                  <div
                    key={msg.id}
                    className={`${styles.bubble} ${styles.user} ${styles.msgSent}`}
                    dir="rtl"
                    lang="ar"
                  >
                    {msg.text}
                  </div>
                );
              }
              if (msg.html) {
                return (
                  <div
                    key={msg.id}
                    className={`${styles.bubble} ${styles.bot} ${styles.msgReceived} ${msg.error ? styles.errorBubble : ''}`}
                    dir="rtl"
                    lang="ar"
                    dangerouslySetInnerHTML={{ __html: msg.html }}
                  />
                );
              }
              return (
                <div
                  key={msg.id}
                  className={`${styles.bubble} ${styles.bot} ${styles.msgReceived} ${msg.error ? styles.errorBubble : ''}`}
                  dir="rtl"
                  lang="ar"
                >
                  {msg.text}
                </div>
              );
            })}
            {typing && (
              <div className={`${styles.typing} ${styles.msgReceived}`} aria-label="يكتب الآن">
                <span /><span /><span />
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <textarea
              ref={inputRef}
              className={styles.input}
              placeholder="اكتب رسالتك هنا..."
              rows={1}
              lang="ar"
              dir="rtl"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  e.stopPropagation();
                  sendMessage();
                }
              }}
            />
            <button
              type="button"
              className={`${styles.send} ${pulsing ? styles.pulsing : ''}`}
              aria-label="إرسال"
              onClick={sendMessage}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 12L20 4L14 20L11 13L4 12Z" fill="#0c0c0d" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
